import { existingNamed } from '@dispositio/core';
import type { LibraryBrowse, LibraryBrowseEntry } from '@dispositio/shared';
import { useMemo, useState, type CSSProperties } from 'react';
import { errorMessage } from '../../api/client';
import { useLibraryBrowse, useLibraryImport, useProjects } from '../../api/hooks';
import { de } from '../../i18n/de';
import { classFocus } from '../../lib/designModel';
import { relativeTime } from '../../lib/relativeTime';
import { useStoryEnv } from '../../state/storyEnv';
import { toast } from '../../state/toast';
import { useWorkspace } from '../../state/workspace';
import { FocusIcon } from '../design/DesignView';
import { Modal } from '../Modal';
import { AssetImg } from '../AssetImg';

const L = de.libraryImport;
type Klass = LibraryBrowse['classes'][number];

const norm = (s: string): string => s.trim().toLocaleLowerCase('de');

/** Kästchen für eine ganze Klasse: an, aus oder teilweise (einige ihrer Einträge gewählt). */
export function ClassCheck({ all, some, label, onChange }: { all: boolean; some: boolean; label: string; onChange: (on: boolean) => void }) {
  return (
    <input
      type="checkbox"
      className="li-check"
      checked={all}
      ref={(el) => {
        if (el) el.indeterminate = some && !all;
      }}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={label}
      title={label}
      data-testid="li-class-check"
    />
  );
}

/**
 * „Importieren“ in der Bibliothek: erst eine andere eigene Geschichte wählen, dann ihre Klassen (Personen, Orte, …) – ganze Klassen
 * ankreuzen oder aufklappen und einzelne Einträge wählen. Vor dem Übernehmen sagt ein Hinweis, dass Kopien entstehen; nach „OK“ legt
 * der Server sie in den gleichnamigen Klassen (und Kategorien) an. Einträge, die es hier schon gibt, werden übersprungen. Aus einer
 * Episode derselben Serie entstehen Referenzen statt Kopien (PLAN.md §43); was hier schon steht, lässt sich nicht noch einmal wählen.
 */
export function LibraryImport({ onClose }: { onClose: () => void }) {
  const { projectId, ctx, story } = useStoryEnv();
  const ws = useWorkspace();
  const projects = useProjects();
  const [sourceId, setSourceId] = useState<string | null>(null);
  const library = useLibraryBrowse(sourceId, projectId);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [query, setQuery] = useState('');
  const [confirming, setConfirming] = useState(false);
  const run = useLibraryImport(projectId);

  const others = useMemo(() => (projects.data ?? []).filter((p) => p.id !== projectId), [projects.data, projectId]);
  const classes = useMemo(
    () =>
      (library.data?.classes ?? [])
        .filter((c) => c.entries.length > 0)
        // was hier schon steht (Referenz derselben Serie), steht hinten und ist nicht wählbar
        .map((c) => ({ ...c, entries: [...c.entries].sort((a, b) => Number(!!a.present) - Number(!!b.present)) }))
        .map((c) => ({ ...c, focus: classFocus(c) }))
        .sort((a, b) => a.focus.order - b.focus.order || a.focus.label.localeCompare(b.focus.label, 'de')),
    [library.data],
  );
  const q = norm(query);
  const matches = (e: LibraryBrowseEntry): boolean => q === '' || norm(e.title).includes(q) || norm(e.category ?? '').includes(q);

  const toggle = (ids: string[], on: boolean): void =>
    setPicked((cur) => {
      const next = new Set(cur);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  const flip = (id: string): void =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const chooseProject = (id: string | null): void => {
    setSourceId(id);
    setPicked(new Set());
    setOpen(new Set());
    setQuery('');
  };

  // gewählte Einträge samt Klasse; welche es hier schon gibt (gleiche Klasse, gleicher Name), wird übersprungen
  const chosen = useMemo(() => classes.flatMap((c) => c.entries.filter((e) => picked.has(e.id) && !e.present).map((e) => ({ entry: e, klass: c }))), [classes, picked]);
  const reference = !!library.data?.reference;
  const skipping = useMemo(() => chosen.filter(({ entry, klass }) => existingNamed(ctx, klass, entry.title)).map(({ entry }) => entry.title), [chosen, ctx]);

  const submit = (): void => {
    if (!sourceId) return;
    run.mutate(
      { sourceProjectId: sourceId, entityIds: chosen.map((c) => c.entry.id) },
      {
        onSuccess: (r) => {
          toast.ok(r.reference ? L.doneRef(r.imported) : L.done(r.imported));
          if (r.skipped.length > 0) toast.info(L.skipped(r.skipped));
          // die Kopien zeigen: liegt keine im offenen Bereich, in den Bereich der ersten wechseln
          const current = ws.designFocus;
          const keys = r.typeIds.map((id) => {
            const t = ctx.types.get(id);
            return t ? classFocus(t).key : (`type:${id}` as const);
          });
          if (keys.length > 0 && !keys.includes(current)) ws.setDesignFocus(keys[0]!);
          onClose();
        },
        onError: (err) => {
          toast.error(errorMessage(err));
          setConfirming(false);
        },
      },
    );
  };

  if (confirming && library.data) {
    const n = chosen.length - skipping.length;
    return (
      <Modal
        title={reference ? L.confirmRefTitle : L.confirmTitle}
        onClose={() => setConfirming(false)}
        footer={
          <>
            <button className="btn" onClick={() => setConfirming(false)} data-testid="li-confirm-cancel">
              {de.common.cancel}
            </button>
            <button className="btn primary" onClick={submit} disabled={run.isPending} autoFocus data-testid="li-confirm-ok">
              {L.ok}
            </button>
          </>
        }
      >
        <div className="li-confirm" data-testid="li-confirm">
          <p>{n > 0 ? (reference ? L.confirmRef(n, library.data.project.name) : L.confirm(n, library.data.project.name)) : L.confirmNothing}</p>
          {n > 0 && skipping.length > 0 && <p className="muted small">{L.confirmSkip(skipping)}</p>}
        </div>
      </Modal>
    );
  }

  const entryRow = (klass: Klass, e: LibraryBrowseEntry) => {
    const title = e.title || L.untitled;
    return (
      <li key={e.id}>
        <label className={`li-entry${e.present ? ' present' : ''}`} title={e.present ? L.presentHint : undefined} data-testid="li-entry" data-title={e.title} data-present={e.present ? 'true' : undefined}>
          <input type="checkbox" className="li-check" checked={!e.present && picked.has(e.id)} disabled={e.present} onChange={(ev) => toggle([e.id], ev.target.checked)} />
          <span className="rb-avatar" style={{ '--c': klass.color } as CSSProperties}>
            {e.imageId ? <AssetImg id={e.imageId} alt="" loading="lazy" /> : <span>{title.slice(0, 1).toUpperCase()}</span>}
          </span>
          <span className="li-entry-title">{title}</span>
          {e.present ? <span className="li-entry-cat">{L.present}</span> : e.category && <span className="li-entry-cat">{e.category}</span>}
        </label>
      </li>
    );
  };

  const tree = (data: LibraryBrowse) => {
    const shown = classes.map((c) => ({ c, entries: c.entries.filter(matches) })).filter((x) => q === '' || x.entries.length > 0);
    return (
      <>
        <div className="li-head">
          <button type="button" className="btn ghost small" onClick={() => chooseProject(null)} data-testid="li-back">
            {L.back}
          </button>
          <strong className="li-project-name">{data.project.name}</strong>
        </div>
        {classes.length === 0 ? (
          <p className="muted">{L.empty(data.project.name)}</p>
        ) : (
          <>
            <input type="search" autoFocus className="rb-search li-search" placeholder={L.search} aria-label={L.search} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="li-search" />
            <div className="li-tree">
              {shown.length === 0 && <p className="muted small">{L.none}</p>}
              {shown.map(({ c, entries }) => {
                const ids = c.entries.filter((e) => !e.present).map((e) => e.id);
                const n = ids.filter((id) => picked.has(id)).length;
                const expanded = open.has(c.id) || q !== '';
                const label = c.focus.label;
                return (
                  <section key={c.id} className="li-class" data-testid="li-class" data-class={c.name}>
                    <div className="li-class-row">
                      <ClassCheck all={ids.length > 0 && n === ids.length} some={n > 0} label={L.wholeClass(label)} onChange={(on) => toggle(ids, on)} />
                      <button type="button" className="li-class-toggle" aria-expanded={expanded} onClick={() => flip(c.id)} title={expanded ? L.collapse(label) : L.expand(label)} data-testid="li-class-toggle">
                        <span className="li-class-icon">
                          <FocusIcon focus={c.focus} size={18} />
                        </span>
                        <span className="li-class-name">{label}</span>
                        <span className="li-count">{L.picked(n, ids.length)}</span>
                        <svg className={`li-chev${expanded ? ' open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </button>
                    </div>
                    {expanded && <ul className="li-entries">{entries.map((e) => entryRow(c, e))}</ul>}
                  </section>
                );
              })}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <Modal
      title={L.title}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            {de.common.cancel}
          </button>
          <button className="btn primary" onClick={() => setConfirming(true)} disabled={!sourceId || chosen.length === 0} data-testid="li-submit">
            {L.submit(chosen.length)}
          </button>
        </>
      }
    >
      <div className="library-import" data-testid="library-import">
        {sourceId === null ? (
          <>
            <p className="muted small">{L.pickProject}</p>
            {projects.isLoading && <p className="muted">{de.common.loading}</p>}
            {projects.data && others.length === 0 && <p className="muted">{L.noProjects}</p>}
            <ul className="li-projects">
              {others.map((p) => (
                <li key={p.id}>
                  <button type="button" className="li-project" onClick={() => chooseProject(p.id)} data-testid="li-project">
                    <span className="li-project-title">{p.name}</span>
                    <span className="muted small">
                      {p.episode !== null && p.seriesId === story.project.seriesId ? `${L.asReference(p.episode)} · ` : ''}
                      {de.projects.edited(relativeTime(p.updatedAt))}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : library.isLoading ? (
          <p className="muted">{de.common.loading}</p>
        ) : library.error ? (
          <p className="error-text">{errorMessage(library.error)}</p>
        ) : library.data ? (
          tree(library.data)
        ) : null}
      </div>
    </Modal>
  );
}
